import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Typography,
  Slider,
  TextField,
  Paper,
  Divider,
  InputAdornment,
  useTheme,
} from "@mui/material";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  numberToWords,
  formatIndianCurrency,
  parseCurrency,
} from "../../../utils/numberToWords";

const EMICalculator = () => {
  const [loanAmount, setLoanAmount] = useState(1000000);
  const [interestRate, setInterestRate] = useState(8.5);
  const [loanTenureYears, setLoanTenureYears] = useState(10);

  // For formatted text input display
  const [loanAmountDisplay, setLoanAmountDisplay] = useState(
    formatIndianCurrency(1000000)
  );

  const [emiResult, setEmiResult] = useState({
    emi: 0,
    totalInterest: 0,
    totalPayment: 0,
    principal: 0,
  });

  const theme = useTheme();

  const LOAN_LIMIT = 100000000; // 10 Cr
  const RATE_LIMIT = 30;
  const TENURE_LIMIT = 30;

  useEffect(() => {
    calculateEMI();
  }, [loanAmount, interestRate, loanTenureYears]);

  const calculateEMI = () => {
    const P = parseFloat(loanAmount);
    const annualRate = parseFloat(interestRate);
    const years = parseInt(loanTenureYears);

    if (isNaN(P) || isNaN(annualRate) || isNaN(years) || P <= 0 || years <= 0) {
      return;
    }

    const N = years * 12;
    const r = annualRate / (12 * 100);
    const emi = (P * r * Math.pow(1 + r, N)) / (Math.pow(1 + r, N) - 1);
    const totalPayment = emi * N;
    const totalInterest = totalPayment - P;

    setEmiResult({
      emi: emi,
      totalInterest: totalInterest,
      totalPayment: totalPayment,
      principal: P,
    });
  };

  const COLORS = ["#e3e3e3", "var(--turquoise-color)"];

  const pieData = [
    { name: "Principal", value: emiResult.principal },
    { name: "Interest", value: emiResult.totalInterest },
  ];

  const formatCurrency = (val) => {
    return `₹${parseFloat(val).toLocaleString("en-IN", {
      maximumFractionDigits: 0,
    })}`;
  };

  const handleLoanChange = (val) => {
    let num = typeof val === "string" ? parseCurrency(val) : val;
    if (num > LOAN_LIMIT) num = LOAN_LIMIT;
    setLoanAmount(num);
    setLoanAmountDisplay(formatIndianCurrency(num));
  };

  const handleRateChange = (val) => {
    let num = val === "" ? "" : parseFloat(val);
    if (num > RATE_LIMIT) num = RATE_LIMIT;
    setInterestRate(num);
  };

  const handleTenureChange = (val) => {
    let num = val === "" ? "" : parseInt(val);
    if (num > TENURE_LIMIT) num = TENURE_LIMIT;
    setLoanTenureYears(num);
  };

  return (
    <Box>
      <Grid container spacing={8}>
        <Grid item xs={12} md={7}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 5 }}>
            {/* Loan Amount */}
            <Box>
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: "#333",
                    fontSize: "0.9rem",
                    textTransform: "uppercase",
                    letterSpacing: 1,
                  }}
                >
                  Loan Amount
                </Typography>
                <TextField
                  size="small"
                  variant="standard"
                  value={loanAmountDisplay}
                  onChange={(e) => handleLoanChange(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">₹</InputAdornment>
                    ),
                    sx: {
                      fontWeight: 800,
                      fontSize: "1.2rem",
                      color: "var(--primary-color)",
                    },
                  }}
                  sx={{ width: "180px" }}
                />
              </Box>
              <Slider
                value={loanAmount || 0}
                min={100000}
                max={LOAN_LIMIT}
                step={50000}
                onChange={(e, val) => handleLoanChange(val)}
                sx={{ color: "var(--primary-color)", height: 8 }}
              />
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="caption" sx={{ color: "#999" }}>
                  1L
                </Typography>
                <Typography variant="caption" sx={{ color: "#999" }}>
                  10Cr
                </Typography>
              </Box>
              <Typography
                variant="caption"
                sx={{
                  color: "var(--primary-color)",
                  fontWeight: 600,
                  mt: 1,
                  display: "block",
                }}
              >
                {numberToWords(loanAmount)}
              </Typography>
            </Box>

            {/* Interest Rate */}
            <Box>
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: "#333",
                    fontSize: "0.9rem",
                    textTransform: "uppercase",
                    letterSpacing: 1,
                  }}
                >
                  Interest Rate (% p.a)
                </Typography>
                <TextField
                  size="small"
                  variant="standard"
                  value={interestRate}
                  onChange={(e) => handleRateChange(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">%</InputAdornment>
                    ),
                    sx: {
                      fontWeight: 800,
                      fontSize: "1.2rem",
                      color: "var(--primary-color)",
                    },
                  }}
                  sx={{ width: "100px" }}
                />
              </Box>
              <Slider
                value={interestRate || 0}
                min={1}
                max={RATE_LIMIT}
                step={0.1}
                onChange={(e, val) => handleRateChange(val)}
                sx={{ color: "var(--primary-color)", height: 8 }}
              />
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="caption" sx={{ color: "#999" }}>
                  1%
                </Typography>
                <Typography variant="caption" sx={{ color: "#999" }}>
                  {RATE_LIMIT}%
                </Typography>
              </Box>
            </Box>

            {/* Tenure */}
            <Box>
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: "#333",
                    fontSize: "0.9rem",
                    textTransform: "uppercase",
                    letterSpacing: 1,
                  }}
                >
                  Loan Tenure (Years)
                </Typography>
                <TextField
                  size="small"
                  variant="standard"
                  value={loanTenureYears}
                  onChange={(e) => handleTenureChange(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">Yrs</InputAdornment>
                    ),
                    sx: {
                      fontWeight: 800,
                      fontSize: "1.2rem",
                      color: "var(--primary-color)",
                    },
                  }}
                  sx={{ width: "100px" }}
                />
              </Box>
              <Slider
                value={loanTenureYears || 0}
                min={1}
                max={TENURE_LIMIT}
                step={1}
                onChange={(e, val) => handleTenureChange(val)}
                sx={{ color: "var(--primary-color)", height: 8 }}
              />
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="caption" sx={{ color: "#999" }}>
                  1 Yr
                </Typography>
                <Typography variant="caption" sx={{ color: "#999" }}>
                  {TENURE_LIMIT} Yrs
                </Typography>
              </Box>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} md={5}>
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: "32px",
              bgcolor: "var(--primary-color)",
              color: "#fff",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 20px 50px rgba(0,0,0,0.1)",
            }}
          >
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <Typography
                variant="overline"
                sx={{ opacity: 0.8, letterSpacing: 2 }}
              >
                Equated Monthly Installment
              </Typography>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,
                  fontFamily: "'Roboto Slab', serif",
                  my: 1,
                  color: "var(--tertiary-color)",
                }}
              >
                {formatCurrency(emiResult.emi)}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "var(--tertiary-color)",
                  opacity: 0.9,
                  fontWeight: 600,
                  display: "block",
                  mb: 2,
                }}
              >
                {numberToWords(emiResult.emi)}
              </Typography>
            </Box>

            <Box sx={{ height: 200, mb: 4 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                        stroke="none"
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value) => formatCurrency(value)}
                    contentStyle={{ borderRadius: "12px", border: "none" }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  px: 2,
                  py: 1.5,
                  bgcolor: "rgba(255,255,255,0.05)",
                  borderRadius: "12px",
                  color: "var(--tertiary-color)",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      bgcolor: COLORS[0],
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ color: "var(--tertiary-color)" }}
                  >
                    Principal Amount
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 700, color: "var(--tertiary-color)" }}
                >
                  {formatCurrency(emiResult.principal)}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  px: 2,
                  py: 1.5,
                  bgcolor: "rgba(255,255,255,0.05)",
                  borderRadius: "12px",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      bgcolor: COLORS[1],
                    }}
                  />
                  <Typography
                    sx={{ color: "var(--tertiary-color)" }}
                    variant="body2"
                  >
                    Total Interest
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 700, color: "var(--tertiary-color)" }}
                >
                  {formatCurrency(emiResult.totalInterest)}
                </Typography>
              </Box>

              <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", my: 1 }} />

              <Box
                sx={{ display: "flex", justifyContent: "space-between", px: 2 }}
              >
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 600, color: "var(--tertiary-color)" }}
                >
                  Total Payable
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 800, color: "var(--tertiary-color)" }}
                >
                  {formatCurrency(emiResult.totalPayment)}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default EMICalculator;
